import React, { useState } from "react";
import { useAutoPoll } from "./useAutoPoll";
import { backendUrl } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { RefreshChip, LoadingState, ErrorState } from "./OpsDashboard";

const LEVELS = [
  { value: "",         label: "All levels" },
  { value: "INFO",     label: "INFO" },
  { value: "WARNING",  label: "WARNING" },
  { value: "ERROR",    label: "ERROR" },
  { value: "CRITICAL", label: "CRITICAL" },
];

const LEVEL_COLORS = {
  INFO:     "bg-neutral-100 text-neutral-700",
  WARNING:  "bg-amber-100 text-amber-800",
  ERROR:    "bg-red-100 text-red-800",
  CRITICAL: "bg-red-600 text-white",
};

export default function OpsLogs() {
  const [level, setLevel] = useState("");
  const [service, setService] = useState("");
  const q = new URLSearchParams({ limit: "200" });
  if (level)   q.set("level", level);
  if (service) q.set("service", service);
  const { data, loading, error, lastUpdated } = useAutoPoll(
    backendUrl(`/api/seo/ops/logs?${q.toString()}`), { intervalMs: 12000 });

  if (error) return <ErrorState message={error} />;
  if (!data)  return <LoadingState />;

  const items = data.items || [];
  const counts = data.counts_24h || {};

  const downloadCsv = () => {
    const rows = [["at", "level", "service", "message", "code", "run_id", "duration_ms"]];
    for (const it of items) {
      rows.push([it.at || "", it.level || "", it.service || "",
                    (it.message || "").replace(/"/g, '""'), it.code || "",
                    it.run_id || "", it.duration_ms ?? ""]);
    }
    const csv = rows.map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `seo-studio-logs-${new Date().toISOString().slice(0, 19)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="p-6" data-testid="ops-logs">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Event log</h1>
          <p className="text-sm text-neutral-500 mt-1">Centralised log — INFO / WARNING / ERROR / CRITICAL across all services.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={downloadCsv}
                    className="text-sm px-3 py-1.5 border border-neutral-200 rounded-md hover:bg-neutral-50"
                    data-testid="logs-export-csv">
            Export CSV
          </button>
          <RefreshChip lastUpdated={lastUpdated} loading={loading} />
        </div>
      </div>

      {/* Counts strip */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        {["INFO", "WARNING", "ERROR", "CRITICAL"].map(l => (
          <div key={l} className="border rounded-lg p-3" data-testid={`logs-count-${l.toLowerCase()}`}>
            <div className={`inline-block px-2 py-0.5 rounded-full text-[10px] ${LEVEL_COLORS[l]}`}>{l}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{counts[l] || 0}</div>
            <div className="text-xs text-neutral-500">last 24 hours</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="flex-row items-center gap-3">
          <CardTitle className="text-base flex-1">Recent events</CardTitle>
          <select value={level} onChange={(e) => setLevel(e.target.value)}
                    className="h-9 border border-neutral-200 rounded-md px-2 text-sm bg-white"
                    data-testid="logs-level-filter">
            {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <Input value={service} onChange={(e) => setService(e.target.value)}
                    placeholder="filter by service…" className="max-w-[220px] h-9"
                    data-testid="logs-service-filter" />
        </CardHeader>
        <CardContent>
          {items.length === 0 && <div className="text-sm text-neutral-500">No events match the filters.</div>}
          {items.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-neutral-500 uppercase tracking-wide">
                  <th className="pb-2">Time</th><th>Level</th><th>Service</th>
                  <th>Message</th><th className="text-right">Duration</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-neutral-100 align-top" data-testid={`log-row-${i}`}>
                    <td className="py-1.5 text-neutral-500 whitespace-nowrap">{it.at?.slice(0, 19).replace("T", " ")}</td>
                    <td>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${LEVEL_COLORS[it.level] || LEVEL_COLORS.INFO}`}>
                        {it.level}
                      </span>
                    </td>
                    <td className="text-neutral-800 font-medium whitespace-nowrap">{it.service}</td>
                    <td className="text-neutral-700">
                      {it.message}
                      {it.code && <span className="ml-2 text-[10px] text-neutral-500">({it.code})</span>}
                    </td>
                    <td className="text-right tabular-nums text-neutral-500">
                      {it.duration_ms != null ? `${Math.round(it.duration_ms)}ms` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
